/**
 * Peças das telas sem o painel e das janelas de conta (proposta "Elements", 17/10/2026):
 *  - AuthLayout: logo + nome no topo, cartão no meio e Termos/Política no pé;
 *  - StateIcon: ícone redondo de estado (neutro; vermelho só no erro, verde só no sucesso);
 *  - SuccessBurst: o "check" de sucesso e o giro de "Levando você ao painel…";
 *  - ScreenDialog: janela sobre o painel (compra, extensão).
 * Estilos em screen.css (prefixo .as-).
 *
 * Movimento (design system de movimento, 23/10/2026): a janela entra e sai com .ds-veu/.ds-dialogo
 * (200 ms entrando, 140 ms saindo, volta do meio) e o foco volta ao gatilho; AbreNoLugar abre
 * espaço para mensagens (.ds-altura); RotuloCarregando troca ícone e rótulo do botão que espera o
 * servidor (.ds-troca + .ds-giro). Os hooks de movimento compartilhados (useHaloUmaVez,
 * useFecharComSaida) ficam em screenText.ts, porque este arquivo só exporta componentes.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBrand } from '../../hooks/useBrand';
import { usePresenca } from '../../hooks/usePresenca';
import { assetUrls, legalUrls } from '../../config/env';
import { useScreenText } from './screenText';
import './screen.css';

export function AuthLayout({
  children,
  wide,
}: {
  children: ReactNode;
  wide?: boolean;
}): JSX.Element {
  const brand = useBrand();
  const { tx } = useScreenText();
  const links = legalUrls[brand.source === 'lead-machine' ? 'lead-machine' : 'eminer'];
  return (
    <div className="as-root as-page">
      <div className="as-brand">
        <img src={assetUrls.logoLeadMachine} alt="" width={36} height={36} />
        <strong>{brand.appTitle}</strong>
      </div>
      <div role="main" className={`as-main${wide ? ' as-main--wide' : ''}`}>
        {children}
      </div>
      <footer className="as-foot">
        <a href={links.terms} target="_blank" rel="noopener noreferrer">
          {tx('terms')}
        </a>
        <a href={links.privacy} target="_blank" rel="noopener noreferrer">
          {tx('privacy')}
        </a>
      </footer>
    </div>
  );
}

export type Tone = 'gold' | 'gray' | 'red' | 'green';

export function StateIcon({ icon, tone = 'gold' }: { icon: string; tone?: Tone }): JSX.Element {
  return (
    <span className={`as-icon as-icon--${tone} material-symbols-rounded`} aria-hidden="true">
      {icon}
    </span>
  );
}

/** Texto com <b>/<a> vindo dos arquivos de idioma (nunca de dados do usuário sem escape). */
export function Html({
  html,
  as: Tag = 'p',
  className,
}: {
  html: string;
  as?: 'p' | 'span' | 'div';
  className?: string;
}): JSX.Element {
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * Sucesso (Ativação concluída, Email confirmado, Acesso liberado). Movimento (23/10/2026): o ✓
 * entra com .ds-sucesso (400 ms, curva de ênfase). Os 10 pontinhos em volta saíram: partículas só
 * nos marcos da regra 9 (M7 b), e a ativação não está entre eles. A barra que enchia em 1,5 s
 * fingia saber quanto falta; no lugar dela, o giro com o texto ("Levando você ao painel…").
 */
export function SuccessBurst({
  title,
  text,
  progress,
}: {
  title: string;
  text?: string;
  progress?: boolean;
}): JSX.Element {
  return (
    <div className="as-success" role="status">
      <span className="as-burst" aria-hidden="true">
        <span className="material-symbols-rounded ds-sucesso">check</span>
      </span>
      <h1>{title}</h1>
      {progress && text ? (
        <p className="as-pulse">
          <span className="as-pulse__giro ds-giro material-symbols-rounded" aria-hidden="true">
            progress_activity
          </span>
          {text}
        </p>
      ) : text ? (
        <p>{text}</p>
      ) : null}
    </div>
  );
}

/** Campos que recebem o foco quando a janela abre (o primeiro deles). */
const CAMPOS =
  'input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled)';

export function ScreenDialog({
  icon,
  iconTone = 'gold',
  title,
  onClose,
  onBack,
  children,
  labelledBy = 'as-dialog-title',
  closeLabel,
  backLabel,
  closeDisabled,
  subtitle,
  className,
  open = true,
  grande = false,
}: {
  icon?: string;
  iconTone?: Tone | 'teal';
  title?: ReactNode;
  onClose?: () => void;
  onBack?: () => void;
  children: ReactNode;
  labelledBy?: string;
  closeLabel: string;
  backLabel?: string;
  closeDisabled?: boolean;
  /** Linha abaixo do título, no cabeçalho. */
  subtitle?: ReactNode;
  /** Classe extra na janela (largura, por exemplo). */
  className?: string;
  /**
   * Movimento: false toca a saída (140 ms; 200 ms na grande) e só então some. Quem desmonta a
   * janela ao fechar não precisa passar nada: ela entra igual, só não anima a saída.
   */
  open?: boolean;
  /** Diálogo grande (janela de busca, LeadExplorer): entra em 280 ms e sai em 200 ms. */
  grande?: boolean;
}): JSX.Element | null {
  const larga = grande || /(^|\s)ds-grande(\s|$)/.test(className ?? '');
  // Movimento (23/10/2026): monta fechada e liga .is-aberto no quadro seguinte (.ds-veu e
  // .ds-dialogo: véu com fade, janela sobe 8 px e vai de 0,97 a 1); fecha pelo caminho inverso,
  // mais rápido, e pode voltar do meio.
  const { montado, aberto } = usePresenca(open, larga ? '--duracao-200' : '--duracao-140');
  const janelaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!onClose || !open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !closeDisabled) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, closeDisabled, open]);

  // Foco nunca se perde: ao abrir, guarda quem tinha o foco (o gatilho); ao fechar (ou desmontar),
  // devolve o foco a ele na hora, sem esperar a saída. Se a pessoa já foi para outro lugar, fica.
  // Efeito passivo de propósito: no de layout, o React devolve o foco ao campo da janela logo
  // depois (restaura a seleção do commit), e quem abre outra janela em seguida perderia o foco.
  useEffect(() => {
    if (!open) return undefined;
    const janela = janelaRef.current;
    const ativo = document.activeElement;
    const gatilho =
      ativo instanceof HTMLElement && ativo !== document.body && !janela?.contains(ativo)
        ? ativo
        : null;
    return () => {
      if (!gatilho?.isConnected) return;
      const agora = document.activeElement;
      if (agora && agora !== document.body && !janela?.contains(agora)) return;
      gatilho.focus({ preventScroll: true });
    };
  }, [open]);

  // A janela nasce invisível (visibility: hidden até .is-aberto), e o autoFocus não pega nela.
  // Aberta, o foco vai para o primeiro campo; sem campo, para a própria janela (o leitor de tela
  // lê o nome dela).
  useEffect(() => {
    if (!aberto) return;
    const janela = janelaRef.current;
    if (!janela || janela.contains(document.activeElement)) return;
    const campo = janela.querySelector<HTMLElement>(CAMPOS);
    if (campo) campo.focus();
    else janela.focus({ preventScroll: true });
  }, [aberto]);

  if (!montado) return null;

  return createPortal(
    <div
      className={`as-root as-overlay ds-veu${aberto ? ' is-aberto' : ''}`}
      role="presentation"
      onClick={(e) =>
        e.target === e.currentTarget && open && onClose && !closeDisabled && onClose()
      }
    >
      <div
        ref={janelaRef}
        className={`as-dialog ds-dialogo${larga ? ' ds-grande' : ''}${aberto ? ' is-aberto' : ''}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? labelledBy : undefined}
        tabIndex={-1}
      >
        {title || onClose || onBack ? (
          <div className="as-dialog__head">
            {onBack ? (
              <button
                type="button"
                className="as-iconbtn ds-resposta"
                aria-label={backLabel ?? closeLabel}
                onClick={onBack}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  arrow_back
                </span>
              </button>
            ) : icon ? (
              <span
                className={`as-dialog__icon as-dialog__icon--${iconTone} material-symbols-rounded`}
                aria-hidden="true"
              >
                {icon}
              </span>
            ) : null}
            {title && subtitle ? (
              <div className="as-dialog__titles">
                <h2 id={labelledBy}>{title}</h2>
                <div className="as-dialog__subtitle">{subtitle}</div>
              </div>
            ) : title ? (
              <h2 id={labelledBy}>{title}</h2>
            ) : (
              <span className="as-grow" />
            )}
            {onClose ? (
              <button
                type="button"
                className="as-iconbtn ds-resposta"
                aria-label={closeLabel}
                disabled={closeDisabled}
                onClick={onClose}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  close
                </span>
              </button>
            ) : null}
          </div>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Mensagem na tela (movimento, 23/10/2026): primeiro abre espaço (.ds-altura, 200 ms) e o texto
 * entra 140 ms depois; ao resolver, o texto sai e o espaço fecha. Fica montada enquanto fecha,
 * com o último conteúdo. O gap da coluna entra junto com a altura (.as-altura em screen.css).
 */
export function AbreNoLugar({
  aberto,
  children,
}: {
  aberto: boolean;
  children: ReactNode;
}): JSX.Element | null {
  const presenca = usePresenca(aberto, '--duracao-200');
  const ultimo = useRef<ReactNode>(children);
  if (aberto) ultimo.current = children;
  if (!presenca.montado) return null;
  return (
    <div className={`ds-altura as-altura${presenca.aberto ? ' is-aberto' : ''}`}>
      <div>
        <div className="ds-altura-conteudo">{aberto ? children : ultimo.current}</div>
      </div>
    </div>
  );
}

/** Espera do catálogo ("Carregando"): abaixo disso, nada de giro, para não piscar. Não é movimento. */
const ESPERA_GIRO = 200;

/** Verdadeiro quando `ativo` já dura mais de 200 ms. */
function useEsperaLonga(ativo: boolean, ms = ESPERA_GIRO): boolean {
  const [passou, setPassou] = useState(false);
  useEffect(() => {
    if (!ativo) {
      setPassou(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setPassou(true), ms);
    return () => window.clearTimeout(timer);
  }, [ativo, ms]);
  return ativo && passou;
}

/**
 * Conteúdo do botão que espera o servidor (movimento, "Carregando"): passados 200 ms, o ícone vira
 * giro e o rótulo diz o que acontece, em troca cruzada de 140 ms (.ds-troca). Os dois rótulos
 * ocupam a mesma célula, então a largura não muda; o que está fora fica aria-hidden. `feito`
 * troca o giro pelo ✓ no mesmo lugar.
 * Botão sem ícone: o giro (e o ✓) entram junto com o rótulo de espera, numa célula sobreposta ao
 * rótulo parado. A célula já nasce com a largura da maior das duas, então o botão não cresce nem
 * o rótulo anda quando o giro aparece.
 */
export function RotuloCarregando({
  ocupado,
  rotulo,
  rotuloOcupado,
  icone,
  feito = false,
}: {
  ocupado: boolean;
  rotulo: string;
  rotuloOcupado?: string;
  icone?: string;
  feito?: boolean;
}): JSX.Element {
  const espera = useEsperaLonga(ocupado);
  const girando = espera && !feito;
  const parado = !girando && !feito;
  const fora = (ligado: boolean): string | undefined => (ligado ? undefined : 'ds-fora');
  const trocaRotulo = girando && Boolean(rotuloOcupado);
  // Ícone, giro e ✓ no mesmo lugar; o ✓ fica sempre montado (fora), e a largura não muda.
  const marca = (
    <span className="ds-troca" aria-hidden="true">
      {icone ? (
        <span className={`material-symbols-rounded${parado ? '' : ' ds-fora'}`}>{icone}</span>
      ) : null}
      {/* O giro só existe enquanto espera; no ✓ ele ainda sai com fade, e some com a janela. */}
      <span className={fora(girando)}>
        {girando || feito ? <span className="as-spinner ds-giro" /> : null}
      </span>
      <span className={`material-symbols-rounded${feito ? '' : ' ds-fora'}`}>check</span>
    </span>
  );
  const texto = rotuloOcupado ? (
    <span className="ds-troca ds-troca-texto">
      <span className={fora(!trocaRotulo)} aria-hidden={trocaRotulo || undefined}>
        {rotulo}
      </span>
      <span className={fora(trocaRotulo)} aria-hidden={!trocaRotulo || undefined}>
        {rotuloOcupado}
      </span>
    </span>
  ) : (
    rotulo
  );
  if (icone) {
    return (
      <>
        {marca}
        {texto}
      </>
    );
  }
  return (
    <span className="ds-troca ds-troca-texto">
      <span className={fora(parado)} aria-hidden={!parado || undefined}>
        {rotulo}
      </span>
      <span className={`as-ocupado${parado ? ' ds-fora' : ''}`} aria-hidden={parado || undefined}>
        {marca}
        {texto}
      </span>
    </span>
  );
}
